import { useEffect, useState } from "react";
import type { AppSettings, PrinterDeliveryMode, PrinterProvider } from "@ai-pos/shared";
import { printerApi, settingsApi } from "../api/resources.js";
import { useAuthStore } from "../stores/authStore.js";

const BRIDGE_WS_URL = `${window.location.origin.replace(/^http/, "ws")}/ws/printer-bridge`;

export function PrinterSettings() {
  const staff = useAuthStore((state) => state.staff);
  // ブリッジトークンの発行・再発行、および接続先（印刷方式・配信方式・IP・ポート）の
  // 変更は管理者専用とする（サーバー側でも検証済み）。前者はトークンを知っていれば
  // 誰でも店舗の印刷ブリッジを名乗って全会計内容を横取り・改ざんできてしまうため、
  // 後者は一般スタッフが自由に変更できるとブリッジ機を任意の宛先へ接続させる
  // SSRFの踏み台にできてしまうため。
  const isAdminOrOwner = staff?.role === "ADMIN" || staff?.role === "OWNER";
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionResult, setConnectionResult] = useState<{ ok: boolean; message: string } | null>(null);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [regeneratingToken, setRegeneratingToken] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => setSettings(res.settings))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  }, []);

  if (!settings) {
    return <div className="stack">{error ? <div className="alert alert-error">{error}</div> : "読み込み中..."}</div>;
  }

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await settingsApi.update({
        printerProvider: settings.printerProvider,
        printerIp: settings.printerIp,
        printerPort: settings.printerPort,
        printerTimeoutMs: settings.printerTimeoutMs,
        printerAutoCut: settings.printerAutoCut,
        printerPaperWidth: settings.printerPaperWidth,
        printerDeliveryMode: settings.printerDeliveryMode,
        printCopies: settings.printCopies,
        autoPrintOnCheckout: settings.autoPrintOnCheckout,
      });
      setSettings(res.settings);
      setMessage("保存しました");
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm("ブリッジトークンを再発行します。既存のブリッジ端末は再設定するまで接続できなくなります。よろしいですか？")) {
      return;
    }
    setRegeneratingToken(true);
    setError(null);
    try {
      const res = await settingsApi.regenerateBridgeToken();
      setRevealedToken(res.printerBridgeToken);
      setSettings((prev) => (prev ? { ...prev, printerBridgeToken: res.printerBridgeToken } : prev));
    } catch (err) {
      setError(err instanceof Error ? err.message : "トークンの再発行に失敗しました");
    } finally {
      setRegeneratingToken(false);
    }
  };

  const handleCheckConnection = async () => {
    setChecking(true);
    setConnectionResult(null);
    try {
      const res = await printerApi.checkConnection();
      setConnectionResult(res.result);
    } catch (err) {
      setConnectionResult({ ok: false, message: err instanceof Error ? err.message : "接続確認に失敗しました" });
    } finally {
      setChecking(false);
    }
  };

  const handleTestPrint = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await printerApi.testPrint();
      setTestResult(res.result);
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : "テスト印刷に失敗しました" });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 640 }}>
      <h2>プリンター設定</h2>
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <div className="card stack">
        <div className="form-field">
          <label htmlFor="printer-provider">印刷方式{!isAdminOrOwner && "（変更は管理者以上のみ）"}</label>
          <select
            id="printer-provider"
            value={settings.printerProvider}
            disabled={!isAdminOrOwner}
            onChange={(e) => setSettings({ ...settings, printerProvider: e.target.value as PrinterProvider })}
          >
            <option value="mock">モック印刷（開発用・実機なし）</option>
            <option value="epson_epos">EPSON ePOS-Print（推奨・TM-m30III-H）</option>
            <option value="escpos_tcp">ESC/POS（TCPソケット、代替方式）</option>
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="printer-delivery-mode">配信方式{!isAdminOrOwner && "（変更は管理者以上のみ）"}</label>
          <select
            id="printer-delivery-mode"
            value={settings.printerDeliveryMode}
            disabled={!isAdminOrOwner}
            onChange={(e) => setSettings({ ...settings, printerDeliveryMode: e.target.value as PrinterDeliveryMode })}
          >
            <option value="direct">直接接続（同一LAN内にバックエンドがある場合）</option>
            <option value="bridge">ブリッジ経由（バックエンドがクラウド上にあり、店舗LAN内に中継ソフトを置く場合）</option>
          </select>
        </div>
        <div className="row">
          <div className="form-field" style={{ flex: 1 }}>
            <label htmlFor="printer-ip">プリンターIPアドレス{!isAdminOrOwner && "（変更は管理者以上のみ）"}</label>
            <input
              id="printer-ip"
              value={settings.printerIp}
              disabled={!isAdminOrOwner}
              onChange={(e) => setSettings({ ...settings, printerIp: e.target.value })}
            />
          </div>
          <div className="form-field" style={{ flex: 1 }}>
            <label htmlFor="printer-port">ポート番号（ESC/POS方式用）{!isAdminOrOwner && "（変更は管理者以上のみ）"}</label>
            <input
              id="printer-port"
              type="number"
              value={settings.printerPort}
              disabled={!isAdminOrOwner}
              onChange={(e) => setSettings({ ...settings, printerPort: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="row">
          <div className="form-field" style={{ flex: 1 }}>
            <label htmlFor="printer-paper-width">用紙幅</label>
            <select
              id="printer-paper-width"
              value={settings.printerPaperWidth}
              onChange={(e) => setSettings({ ...settings, printerPaperWidth: Number(e.target.value) })}
            >
              <option value={58}>58mm（32桁）</option>
              <option value={80}>80mm（48桁）</option>
            </select>
          </div>
        </div>
        <div className="row">
          <div className="form-field" style={{ flex: 1 }}>
            <label htmlFor="printer-timeout">タイムアウト（ミリ秒）</label>
            <input
              id="printer-timeout"
              type="number"
              value={settings.printerTimeoutMs}
              onChange={(e) => setSettings({ ...settings, printerTimeoutMs: Number(e.target.value) })}
            />
          </div>
          <div className="form-field" style={{ flex: 1 }}>
            <label htmlFor="print-copies">印刷枚数</label>
            <input
              id="print-copies"
              type="number"
              min={1}
              max={5}
              value={settings.printCopies}
              onChange={(e) => setSettings({ ...settings, printCopies: Number(e.target.value) })}
            />
          </div>
        </div>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.printerAutoCut}
            onChange={(e) => setSettings({ ...settings, printerAutoCut: e.target.checked })}
            style={{ minHeight: "auto", width: 20, height: 20 }}
          />
          自動カットON
        </label>
        <label className="row" style={{ gap: 6 }}>
          <input
            type="checkbox"
            checked={settings.autoPrintOnCheckout}
            onChange={(e) => setSettings({ ...settings, autoPrintOnCheckout: e.target.checked })}
            style={{ minHeight: "auto", width: 20, height: 20 }}
          />
          会計確定時に自動印刷する
        </label>

        <button className="btn btn-primary btn-lg" onClick={() => void handleSave()} disabled={saving}>
          保存
        </button>
      </div>

      {settings.printerDeliveryMode === "bridge" && (
        <div className="card stack">
          <h3>プリンターブリッジ設定</h3>
          <p style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
            店舗LAN内の端末（このMac等）で <code>apps/printer-bridge</code> を常時起動し、下記の値を <code>.env</code>{" "}
            に設定してください。
          </p>
          <div className="form-field">
            <label>接続先（BACKEND_WS_URL）</label>
            <input readOnly value={BRIDGE_WS_URL} onFocus={(e) => e.target.select()} />
          </div>
          <div className="form-field">
            <label>ブリッジトークン（BRIDGE_TOKEN）</label>
            {revealedToken ? (
              <input readOnly value={revealedToken} onFocus={(e) => e.target.select()} />
            ) : (
              <input readOnly value={settings.printerBridgeToken || "未発行"} />
            )}
          </div>
          {isAdminOrOwner && (
            <div className="row">
              <button className="btn" onClick={() => void handleRegenerateToken()} disabled={regeneratingToken}>
                {regeneratingToken ? "発行中..." : settings.printerBridgeToken ? "トークンを再発行" : "トークンを発行"}
              </button>
            </div>
          )}
          {revealedToken && (
            <div className="alert alert-success">
              このトークンは今だけ表示されます。ブリッジ端末の.envに保存してください。画面を離れると再確認できません。
            </div>
          )}
        </div>
      )}

      <div className="card stack">
        <h3>接続確認・テスト印刷</h3>
        <p style={{ color: "var(--color-text-muted)", fontSize: 13 }}>
          実機のePOS-Print XMLエンドポイントや文字コード設定は機種・ファームウェアにより異なる場合があります。実機接続後、必ずテスト印刷で確認してください（docs/PRINTING.md参照）。
        </p>
        <div className="row">
          <button className="btn" onClick={() => void handleCheckConnection()} disabled={checking}>
            {checking ? "確認中..." : "接続確認"}
          </button>
          <button className="btn" onClick={() => void handleTestPrint()} disabled={testing}>
            {testing ? "印刷中..." : "テスト印刷"}
          </button>
        </div>
        {connectionResult && (
          <div className={connectionResult.ok ? "alert alert-success" : "alert alert-error"}>{connectionResult.message}</div>
        )}
        {testResult && <div className={testResult.success ? "alert alert-success" : "alert alert-error"}>{testResult.message}</div>}
      </div>
    </div>
  );
}
